import { useState, useContext, useRef, useMemo } from 'react';
import { useMatch, Link, useLocation } from 'react-router-dom';
import { alpha } from '@classify/react-core-components/styles';
import { Helmet } from 'react-helmet';

import { List } from '@mui/material';
import { ROUTES } from '@openclassrooms/routing';
import Box from '@classify/react-core-components/Box';
import ContentWrapper from '@classify/react-core-components/ContentWrapper';
import PageLoading from '@classify/react-core-components/PageLoading';
import { LearningOutcomeListItem } from '@openclassrooms/ui-web/learningOutcomes';
import { RichContent } from '@openclassrooms/ui-web/rich-content';
import { styled } from '@classify/react-core-components/styles';

import Button from '@classify/react-core-components/Button';
import Typography from '@classify/react-core-components/Typography';
import { translate } from '@openclassrooms/translations';
import Divider from '@classify/react-core-components/Divider';
import { getNextChapter } from '@openclassrooms/ui-web/table-of-content';
import ArrowForward from '@mui/icons-material/ArrowForward';

import Callout from '@classify/react-core-components/Callout';
import { STATUSES } from '@openclassrooms/ui-web/learning-activities';
import { ImageCdn } from '@openclassrooms/images-cdn';

import CourseEditionPanel from '../components/CourseEditionPanel';
import EditionPanelVariants from '../constants/EditionPanelVariants';
import QuizQuestions from '../components/QuizQuestions';
import CourseChapterHeader from '../components/CourseChapterHeader';
import TocToggler from '../components/TableOfContent/TocToggler';
import CourseContext from '../contexts/courseContext';
import CourseExerciseContext from '../contexts/courseExerciseContext';
import CourseNavigationFooter from '../components/CourseNavigationFooter';
import getFooterInformation from '../services/getFooterInformation';
import QuizCompletionBanner from '../components/QuizCompletionBanner';
import quizSuccess from '../images/marketing_success.svg';
import { QUIZ_COMPLETED_STATUSES } from '../constants/LearningStatuses';
import BannerWrapper from '../components/TrainingMerchandising/BannerWrapper/BannerWrapper';
import BigInfoBanner from '../components/TrainingMerchandising/BigInfoBanner';
import CourseTitle from '../components/CourseTitle';

const ProgressionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isQuizCompleted',
})<{ isQuizCompleted: boolean }>(({ theme: { palette }, isQuizCompleted }) => ({
  backgroundColor: 'initial',
  borderColor: 'var(--categoryColor) !important',
  color: 'var(--categoryColor) !important',
  '&:hover': {
    color: 'var(--categoryColor)',
    backgroundColor: 'var(--hoverBackgroundColor)',
    borderColor: 'var(--categoryColor)',
  },

  ...(isQuizCompleted && {
    backgroundColor: 'var(--categoryColor)',
    borderColor: 'var(--categoryColor)',
    color: palette.common.white,
    '&:hover': {
      color: palette.common.white,
      backgroundColor: 'var(--categoryColor)',
    },
  }),
}));

const Content = styled(RichContent)(({ theme: { spacing } }) => ({
  margin: spacing(3, 0, 8),
  '& > p': {
    margin: [0, '!important'],
  },
}));

const QuizInformationWrapper = styled('div')(({ theme: { spacing, breakpoints } }) => ({
  padding: spacing(2, 2, 0, 2),
  [breakpoints.up('sm')]: {
    padding: spacing(3, 3, 0, 3),
  },
}));

const ExerciseWrapper = styled('div')(({ theme: { spacing, palette } }) => ({
  border: `1px solid ${palette.stroke}`,
  borderRadius: spacing(0.5),
  marginBottom: spacing(4),
}));

const ExerciseContentWrapper = styled(ContentWrapper)(({ theme: { spacing } }) => ({
  padding: spacing(4, 2),
  width: 'fill-available',
}));

const { STATUS_PASSED } = STATUSES;
const IS_IOS_APP_URL_PARAM = 'is_ios_app';

const CourseExerciseScreen = () => {
  const match = useMatch(ROUTES.COURSES.EXERCISES);
  const { pathname } = useLocation();
  const { course, flatToc, canEditCourse } = useContext(CourseContext);
  const headerRef = useRef<HTMLDivElement>(null);

  const [isTocOpen, setIsTocOpen] = useState(true);

  const { exerciseId, exerciseSession, quizInformation } = useContext(CourseExerciseContext);

  const [hasSubmittedQuiz, setSubmittedQuiz] = useState(false);

  const { learningActivityStatus: quizLearningStatus, reinitializationAvailableAt } =
    quizInformation || {};

  const searchParams = new URLSearchParams(window.location.search);

  const isInWebView = searchParams.has(IS_IOS_APP_URL_PARAM)
    ? searchParams.get(IS_IOS_APP_URL_PARAM) === 'true'
    : false;

  const memoizedToc = useMemo(
    () => <TocToggler headerRef={headerRef} isOpen={isTocOpen} setIsOpen={setIsTocOpen} />,
    [isTocOpen]
  );

  if (!exerciseSession || !course) {
    return <PageLoading pastDelay />;
  }

  const { title, description, exerciseLearningOutcomes, questions } = exerciseSession;

  const nextChapter = getNextChapter(flatToc, Number(exerciseId));
  const { label, url } = getFooterInformation(nextChapter, course);
  const isQuizCompleted = Boolean(exerciseSession?.completedAt);

  return (
    <div
      style={{
        '--categoryColor': course.categoryColor,
        '--hoverBackgroundColor': alpha(course.categoryColor, 0.8),
      }}
    >
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>

      {!isInWebView && <CourseChapterHeader headerRef={headerRef} />}

      {quizLearningStatus && QUIZ_COMPLETED_STATUSES.includes(quizLearningStatus) && (
        <QuizCompletionBanner
          quizLearningStatus={quizLearningStatus}
          quizReinitializationDate={reinitializationAvailableAt}
        />
      )}

      <Box sx={{ display: 'flex', position: 'relative', pb: 2 }}>
        {!isInWebView && memoizedToc}

        <ExerciseContentWrapper variant="narrow">
          {canEditCourse && (
            <CourseEditionPanel exerciseId={`${exerciseId}`} variant={EditionPanelVariants.QUIZ} />
          )}

          {!isInWebView && <CourseTitle>{title}</CourseTitle>}

          {hasSubmittedQuiz && quizLearningStatus === STATUS_PASSED && (
            <Callout
              sx={{ mb: 5 }}
              type="success"
              illustrationElement={<ImageCdn src={quizSuccess} alt="" />}
            >
              <>
                <Typography variant="h6">
                  {translate('course.chapter.quiz.successfully_completed.label')}
                </Typography>
                <Typography variant="body">
                  {translate('course.chapter.quiz.successfully_completed.desc')}
                </Typography>
              </>
            </Callout>
          )}

          <ExerciseWrapper>
            {exerciseLearningOutcomes && (
              <QuizInformationWrapper>
                <Typography variant="h3" component="h2">
                  {translate('mcq.view.message.skill_title')}
                </Typography>
                <Divider />

                <List sx={{ my: 2 }}>
                  {exerciseLearningOutcomes.map(({ name, learningOutcomeId }) => (
                    <LearningOutcomeListItem title={name} key={learningOutcomeId} hasSmallIcon />
                  ))}
                </List>
              </QuizInformationWrapper>
            )}

            {description && (
              <QuizInformationWrapper>
                <Typography variant="h3" component="h2">
                  {translate('mcq.view.message.description_title')}
                </Typography>
                <Divider />
                <Content content={description} />
              </QuizInformationWrapper>
            )}

            {questions && <QuizQuestions onSubmit={setSubmittedQuiz} />}
          </ExerciseWrapper>

          <BannerWrapper>
            <BigInfoBanner ctaDataEvent="Navigation|Redirection::Internal|Quiz Apply" />
          </BannerWrapper>
        </ExerciseContentWrapper>
      </Box>
      {!isInWebView && (
        <CourseNavigationFooter
          currentPath={match.path}
          categoryColor={course.categoryColor}
          nextChapter={nextChapter}
        >
          <ProgressionButton
            to={url}
            component={Link}
            variant={isQuizCompleted ? 'primary' : 'secondary'}
            endIcon={<ArrowForward />}
            isQuizCompleted={isQuizCompleted}
            role="link"
            title={nextChapter?.title}
            state={{ from: pathname }}
          >
            {label}
          </ProgressionButton>
        </CourseNavigationFooter>
      )}
    </div>
  );
};

export default CourseExerciseScreen;
